import { get } from '@rover/react-lib/src/apis/serviceLinks.api';
import type {
  ActionCreator,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { effects as citiesAndNeighborhoodsEffects } from '@rover/react-lib/src/redux/ducks/citiesAndNeighborhoods.duck';
import generateActions from '@rover/react-lib/src/redux/ducks/generateActions';
import { ApiServiceLinksRetrieve200DataItem } from '@rover/rsdk/src/apiClient/latest';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';
import {
  CountryUrlPrefix,
  getCountryUrlPrefix,
  US_COUNTRY_URL_PREFIX,
} from '@rover/shared/js/constants/roverGeo.constants';
import getPath from '@rover/shared/js/utils/getPath';
import {
  DISPLAY_NAME_FOR_SERVICE_LANDING_ENUM,
  isServiceLanding,
  SEO_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

export type FrontendRoutes = Record<string, RegExp[]>;

export type GetServiceLandingType = (path: string) => ServiceLandingEnum | null;

export type State = {
  loading: boolean;
  errorMessage: string;
  fetchServiceLinksResponse: ApiServiceLinksRetrieve200DataItem[] | null;
};

type Actions = {
  fetchServiceLinksPending: ActionCreator<void>;
  fetchServiceLinksFailure: ActionCreator<string>;
  fetchServiceLinksSuccess: ActionCreator<ApiServiceLinksRetrieve200DataItem[] | undefined>;
};

type Effects = {
  fetchServiceLinks: EffectCreator<void>;
  earlyPrefetch: EffectCreator<void>;
};

export type ServiceLinks = Record<ServiceLandingEnum, string>;
export type ServiceLinksByCountry = Record<CountryUrlPrefix, ServiceLinks>;
export type ServiceLinkWithName = ApiServiceLinksRetrieve200DataItem & { name: string };

type Selectors = {
  getServiceLinks: SelectorCreator<ApiServiceLinksRetrieve200DataItem[] | null>;
  getServiceLinksWithDisplayName: SelectorCreator<ServiceLinkWithName[]>;
  getServiceLinksLoading: SelectorCreator<boolean>;
  getServiceLandingType: SelectorCreator<GetServiceLandingType>;
  getServiceLinksByCountry: SelectorCreator<ServiceLinksByCountry | null>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'serviceLinks',
  initialState: {
    loading: false,
    errorMessage: '',
    fetchServiceLinksResponse: null,
  },
  actions: {
    ...generateActions('fetchServiceLinks'),
  },
  effects: {
    fetchServiceLinks:
      () =>
      async (
        dispatch,
        getState,
        { fetchServiceLinksPending, fetchServiceLinksSuccess, fetchServiceLinksFailure }
      ) => {
        dispatch(fetchServiceLinksPending());

        try {
          const { data: serviceLinks } = await get();
          dispatch(fetchServiceLinksSuccess(serviceLinks));
        } catch (e) {
          dispatch(fetchServiceLinksFailure((e as Error).message));
          captureError(e as Error);
        }
      },
    earlyPrefetch:
      () =>
      async (dispatch, getState, actions, { fetchServiceLinks }, { getServiceLandingType }) => {
        const path = getPath();
        const countryUrlPrefix = getCountryUrlPrefix(path) ?? US_COUNTRY_URL_PREFIX;

        await dispatch(fetchServiceLinks());
        const serviceLandingType =
          getServiceLandingType(getState())(getPath()) || ServiceLandingEnum.DOG_BOARDING;

        const serviceType = SEO_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];
        await dispatch(
          citiesAndNeighborhoodsEffects.fetchCitiesAndNeighborhoods({
            countryUrlPrefix,
            serviceType,
          })
        );
      },
  },
  selectors: {
    getServiceLinks: (getState, createSelector) =>
      createSelector([getState], (x: State) => x.fetchServiceLinksResponse),
    getServiceLinksWithDisplayName: (getState, createSelector, { getServiceLinks }) =>
      createSelector([getServiceLinks], (serviceLinks) =>
        serviceLinks.map(({ type, href }: { type: ServiceLandingEnum; href: string }) => {
          return {
            type,
            href,
            name: DISPLAY_NAME_FOR_SERVICE_LANDING_ENUM()[type],
          };
        })
      ),
    getServiceLinksLoading: (getState, createSelector) =>
      createSelector([getState], (x: State) => x.loading),
    getServiceLandingType: (getState, createSelector, { getServiceLinks }) =>
      createSelector([getServiceLinks], (serviceLinks) => (path: string /* a URL pathname */) => {
        if (!serviceLinks) {
          captureError(new Error('Service links routing data is not available'));
          return null;
        }

        const serviceLink = serviceLinks.find(({ href }) => path.startsWith(href));

        if (!serviceLink) {
          captureError(new Error(`Could not match path "${path}" to a valid service landing page`));
          return null;
        }

        if (!isServiceLanding(serviceLink.type)) {
          captureError(
            new TypeError(
              `Found a service landing page for "${path}", but "${serviceLink.type}" is not a valid type`
            )
          );
          return null;
        }

        return serviceLink.type;
      }),
    getServiceLinksByCountry: (getState, createSelector, { getServiceLinks }) =>
      createSelector(
        [getServiceLinks],
        (serviceLinks: ApiServiceLinksRetrieve200DataItem[] | null) => {
          if (!serviceLinks) return null;
          const serviceLinksByCountry = {} as ServiceLinksByCountry;

          serviceLinks.forEach(({ type, href }) => {
            const countryCode = getCountryUrlPrefix(href) ?? US_COUNTRY_URL_PREFIX;

            if (!(countryCode in serviceLinksByCountry)) {
              serviceLinksByCountry[countryCode] = {} as ServiceLinksByCountry[typeof countryCode];
            }

            if (type) {
              serviceLinksByCountry[countryCode][type] = href;
            }
          });

          return serviceLinksByCountry;
        }
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck<
  State,
  Actions,
  Effects,
  Selectors
>(options);
